/**
 * `FormAnswersView` — renderização **read-only** de um par
 * `(schema, answers)` do subsistema de Forms (PR-M1-01e).
 *
 * Diferente do `FormRenderer` (interativo, com submit), este
 * componente mostra as respostas formatadas e humanas para quem
 * **consulta** uma submissão: Karla no Superadmin vendo o que o lead
 * respondeu no questionário, clínica vendo o intake do paciente
 * (futuro M9), etc.
 *
 * **Princípios:**
 *   - Zero hardcode de cor/fonte — tokens do DS (`text-foreground`,
 *     `text-muted-foreground`, `border-border`, `bg-card`).
 *   - Mobile-first — layout em `<dl>` que stack em mobile e vira
 *     grid label/valor em `sm+`.
 *   - Respeita `visible_if` — não mostra campo que estava oculto no
 *     momento da resposta.
 *   - Formata por tipo (cnpj mascarado, multiselect como chips,
 *     select com label humano em vez do value, etc).
 *   - Semântico — `<section>` + `<dl>/<dt>/<dd>` com `aria-*` onde
 *     faz sentido. Boa leitura por screen reader.
 *
 * Pega o schema original do template (mesmo que a versão tenha mudado
 * depois) — a versão do template é preservada em `submission.template_version`
 * mas hoje a edge function entrega o schema vigente. Mismatch é raro
 * (Karla publica uma v2, respostas antigas continuam renderizáveis
 * porque o schema novo costuma só **adicionar** campos).
 */
import { Fragment, type ReactNode } from 'react';

import type { FormField, FormSchema, FormSection } from '../schemas.js';

import { formatCepAnswer, formatCnpjAnswer, formatTelAnswer } from './formatters.js';
import { isVisible } from './logic/visibleIf.js';

export interface FormAnswersViewProps {
  /** Schema do template usado pela submissão. */
  schema: FormSchema;
  /** Respostas finais (coluna `answers` da submission). */
  answers: Record<string, unknown>;
  /** Prefixo de IDs para links internos (anchors, aria-describedby). */
  idPrefix?: string;
}

/** Resolve o label humano de uma option a partir do value bruto. */
function resolveOptionLabel(field: FormField, value: string): string {
  const opt = field.options?.find((o) => o.value === value);
  return opt ? opt.label : value;
}

/** Renderiza o valor da resposta formatado conforme o tipo do campo.
 * Retorna `null` se a resposta for vazia (visual "sem resposta"). */
function renderAnswer(field: FormField, raw: unknown): ReactNode {
  // Tratamento explícito de vazio — `null`, `undefined`, string vazia,
  // array vazio. Evita cair no else e printar "null" literal.
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'string' && raw.trim().length === 0) return null;
  if (Array.isArray(raw) && raw.length === 0) return null;

  switch (field.type) {
    case 'tel':
      return <span>{typeof raw === 'string' ? formatTelAnswer(raw) : String(raw)}</span>;
    case 'cnpj':
      return (
        <span className="font-mono text-xs sm:text-sm">
          {typeof raw === 'string' ? formatCnpjAnswer(raw) : String(raw)}
        </span>
      );
    case 'cep':
      return (
        <span className="font-mono text-xs sm:text-sm">
          {typeof raw === 'string' ? formatCepAnswer(raw) : String(raw)}
        </span>
      );
    case 'textarea':
      return (
        <span className="block whitespace-pre-wrap">
          {typeof raw === 'string' ? raw : String(raw)}
        </span>
      );
    case 'select':
    case 'radio':
      return <span>{typeof raw === 'string' ? resolveOptionLabel(field, raw) : String(raw)}</span>;
    case 'multiselect':
      if (!Array.isArray(raw)) return <span>{String(raw)}</span>;
      return (
        <ul className="flex flex-wrap gap-1.5">
          {(raw as unknown[]).map((v, idx) => {
            const value = typeof v === 'string' ? v : String(v);
            return (
              <li
                key={`${field.id}-${idx}-${value}`}
                className="inline-flex items-center rounded-md border border-border bg-muted px-2 py-0.5 text-xs text-foreground"
              >
                {resolveOptionLabel(field, value)}
              </li>
            );
          })}
        </ul>
      );
    case 'email':
    case 'text':
    default:
      return <span>{typeof raw === 'string' ? raw : String(raw)}</span>;
  }
}

/** Renderiza um par pergunta/resposta — a primitiva visual do view. */
function AnswerRow({
  field,
  answers,
  idPrefix,
}: {
  field: FormField;
  answers: Record<string, unknown>;
  idPrefix: string;
}) {
  if (!isVisible(field.visible_if, answers)) return null;

  const raw = answers[field.id];
  const rendered = renderAnswer(field, raw);
  const rowId = `${idPrefix}-${field.id}`;

  return (
    <Fragment>
      <div className="grid gap-1 py-3 first:pt-0 sm:grid-cols-[minmax(180px,1fr)_2fr] sm:gap-4">
        <dt id={rowId} className="text-sm font-medium text-muted-foreground sm:pt-0.5">
          {field.label}
          {field.required ? (
            <span aria-hidden="true" className="ml-1 text-muted-foreground/70">
              *
            </span>
          ) : null}
        </dt>
        <dd aria-labelledby={rowId} className="text-sm text-foreground sm:text-base">
          {rendered ?? <span className="italic text-muted-foreground">Sem resposta</span>}
        </dd>
      </div>
      {/* Follow-up e other_field vêm depois do principal, recursivos
          em 1 nível (limite prático do subsistema — ver schemas.ts). */}
      {field.follow_up ? (
        <AnswerRow field={field.follow_up} answers={answers} idPrefix={idPrefix} />
      ) : null}
      {field.other_field ? (
        <AnswerRow field={field.other_field} answers={answers} idPrefix={idPrefix} />
      ) : null}
    </Fragment>
  );
}

/** Renderiza uma seção do schema como bloco visual com título + lista. */
function SectionBlock({
  section,
  answers,
  idPrefix,
}: {
  section: FormSection;
  answers: Record<string, unknown>;
  idPrefix: string;
}) {
  return (
    <section className="space-y-2">
      <header>
        <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {section.title}
        </h4>
        {section.description ? (
          <p className="text-xs text-muted-foreground">{section.description}</p>
        ) : null}
      </header>
      <dl className="divide-y divide-border">
        {section.questions.map((field) => (
          <AnswerRow key={field.id} field={field} answers={answers} idPrefix={idPrefix} />
        ))}
      </dl>
    </section>
  );
}

export function FormAnswersView({
  schema,
  answers,
  idPrefix = 'form-answers',
}: FormAnswersViewProps) {
  return (
    <div className="space-y-6">
      {schema.sections.map((section) => (
        <SectionBlock
          key={section.id}
          section={section}
          answers={answers}
          idPrefix={`${idPrefix}-${section.id}`}
        />
      ))}
    </div>
  );
}
